
import Vue from 'vue';
import { AdFormOptions, AdFormPlacementType, AdFormProps } from '@ads/types/AdForm';
import { AdsWindow } from '@ads/types/window';
import { loadScript } from '@ads/utils/scriptLoader';
import getAdxAdid from '@ads/utils/getAdxAdid';
import { adx as adxUrl } from '@ads/config/api.config';
import adsConfig from '@ads/config/adForm.config';
import AdBlockFallback from '@ads/components/adBlock/AdBlockFallback.vue';

interface Data {
  divId: string;
  adFormType: AdFormPlacementType | null;
  isMounted: boolean;
  hasBeenRendered: boolean;
  isFallback?: boolean;
  adBlockFallback?: AdFormOptions['adBlockFallback'];
  observer: MutationObserver | null;
}

interface Computed {
  classModifier: Record<string, unknown>;
  disableAd: boolean;
  isMobileOrTablet: boolean;
  disabledAdsList: string[];
  adRenderingState: boolean;
  idModifier: string | null;
  isAdBlocked?: boolean;
}

interface Props {
  adType: string;
  uniqueIdentifier: string;
  settings: AdFormOptions;
}

interface AdFormType {
  type: AdFormPlacementType | null;
  isFallback: boolean;
}

interface Methods {
  adProperties: (placements: Record<string, AdFormOptions>) => AdFormProps;
  initAdx: () => Promise<void>;
  getAdFormType: (adType: Props['adType']) => AdFormType;
  placeAd: (adProps: AdFormProps) => void;
  placeOutstreamAd: (placement: string, adProps: AdFormProps) => void;
  parseDiMatterAd: (placement: string) => string[] | boolean;
}
declare const window: AdsWindow;

const mobileAds = adsConfig.mobileAds as AdFormPlacementType[];
const mobileFallbackAds = adsConfig.mobileFallbackAds as Record<string, AdFormPlacementType>;

export default Vue.extend<Data, Methods, Computed, Props>({
  name: 'AdFormBanner',
  components: {
    AdBlockFallback,
  },
  props: {
    adType: {
      type: String,
      default: '',
      required: false,
    },
    uniqueIdentifier: {
      type: String,
      default: '',
      required: false,
    },
    settings: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  data() {
    return {
      divId: '',
      adFormType: null,
      isMounted: false,
      hasBeenRendered: false,
      isFallback: false,
      adBlockFallback: undefined,
      observer: null,
    };
  },
  computed: {
    isMobileOrTablet() {
      return (typeof window !== 'undefined' && window.innerWidth <= 991) || false;
    },
    disableAd() {
      return this.disabledAdsList.includes(this.adType);
    },
    disabledAdsList() {
      return this.$store.getters['adForm/getDisabledAdsList'];
    },
    classModifier() {
      return { [`C-banner--${this.adFormType}`]: this.adFormType };
    },
    adRenderingState() {
      return this.$store.getters['adForm/setAdsRenderingState'];
    },
    idModifier() {
      let id = null;

      if (this.adFormType === 'header') {
        id = 'header-top-banner';
      }

      return id;
    },
    isAdBlocked() {
      return this.$store.getters['adForm/getAdBlockLayerState'];
    },
  },
  watch: {
    async adRenderingState(canRender) {
      if (canRender && !this.$isServer) {
        await this.initAdx();
      }
    },
    /*
    async disableAd(isDisabled, wasDisabled) {
      // Trigger this check after user with subscriber/adFree account logs out
      if (!isDisabled && wasDisabled && this.hasBeenRendered) {
        this.hasBeenRendered = false;
        await this.$nextTick();
        await this.initAdx();
      }
    },
    */
  },
  methods: {
    adProperties(placements) {
      const keywords: string[] = this.$store.getters['adForm/getAdsKeywords'];
      const keyValues: string[] = this.$store.getters['adForm/getAdsKeyValues'];

      const settingsMkw = this.settings.mkw || [];
      const settingsMkv = this.settings.mkv || [];

      const mark = this.settings.mark || false;

      const adProps: AdFormProps = {
        dom: this.settings.divId || this.divId,
        ...placements[this.adFormType as AdFormPlacementType], // Channel config ads section placements settings
        ...this.settings, // Prop placements settings
        mkw: [...keywords, ...settingsMkw],
        mkv: [...keyValues, ...settingsMkv],
        mark,
      };

      return adProps;
    },
    async initAdx() {
      const adFormType = this.getAdFormType(this.adType);
      this.adFormType = adFormType.type;
      this.isFallback = adFormType.isFallback;

      if (typeof window.adformtag === 'undefined') {
        return;
      }

      if (this.disableAd || !this.adRenderingState || this.hasBeenRendered) {
        return;
      }

      if (!this.isMobileOrTablet && !this.isFallback && mobileAds.find((ad) => ad === this.adFormType)) {
        return;
      }

      if (!this.adFormType && !this.isFallback) {
        console.log(`[portal-ads] Invalid adType given or fallback type is not available for ${this.adType}`);
        return;
      }

      const placements: Record<string, AdFormOptions> = this.$store.getters['adForm/getAdsPlacements'];
      const adProps: AdFormProps = this.adProperties(placements || {});

      window.adxadid = getAdxAdid();
      if (window.adxadid) {
        window.adformtag.push(function () {
          window.adformtag.setRequestParam('adid', window.adxadid);
        });
      }

      // Export mkv and mkw values to window for check-ups
      window.d2_adx_mkv = adProps.mkv || [];
      window.d2_adx_mkw = adProps.mkw || [];

      // Parse in keys for dimatter
      if (adProps.mkw?.length) {
        const _attachData = window.dimatter.find((v: string[]) => v[0] === '_attachData') || null;
        let dimatter_keys: Array<string> = [];

        const pathParts = window.location.pathname.split('/');

        // Remove last element if empty
        if (pathParts[pathParts.length - 1] === '') {
          pathParts.pop();
        }

        pathParts.shift();
        dimatter_keys = dimatter_keys.concat(pathParts);

        for (let i = 0; i < adProps.mkw.length; i++) {
          if (adProps.mkw[i].indexOf('ck-') === 0) {
            dimatter_keys.push(adProps.mkw[i].substring(3).replace(/-/g, ' '));
          }
        }

        // Remove duplicates
        dimatter_keys = [...new Set(dimatter_keys)];

        if (dimatter_keys.length && typeof window.dimatter !== 'undefined' && !_attachData) {
          // Push in new _attachData value
          window.dimatter.push(['_attachData', 'ck-keys', dimatter_keys]);
        } else {
          // Replace existing _attachData value
          const _attachDataIndex = window.dimatter.findIndex((v: string[]) => v[0] === '_attachData');
          window.dimatter[_attachDataIndex] = ['_attachData', 'ck-keys', dimatter_keys];
        }
      }

      this.placeAd(adProps);
      this.hasBeenRendered = true;
    },
    placeAd(adProps) {
      const placement = this.isFallback && adProps.fallback ? adProps.fallback : adProps.mid || '';
      if (placement === '') {
        return;
      }

      // Detect DigitalMatter ad placement and parse its properties
      const dimatter = this.parseDiMatterAd(placement);
      if (Array.isArray(dimatter) && typeof window._dlf.ibbtag !== 'undefined') {
        this.divId = dimatter[0];
        if (dimatter[1]) {
          window._dlf.ibbtag(dimatter[0], dimatter[1], { dom: this.divId, mark: adProps.mark });
        } else {
          window._dlf.ibbtag(dimatter[0], { dom: this.divId, mark: adProps.mark });
        }
        return;
      }

      // Reset targeting
      window.adformtag.resetTargeting(placement);

      // Render Adform placement
      const options = {
        dom: adProps.dom,
        mark: adProps.mark,
        keys: adProps.mkw,
        kv: adProps.mkv,
      };

      // Outstream ad placement between 1st and 2nd square positions
      const placements: Record<string, AdFormOptions> = this.$store.getters['adForm/getAdsPlacements'];
      const outstreamPlacement = placements.outstream && placements.outstream.mid && placements.outstream.mid[0];
      const index = this.settings.index;
      if (!this.isMobileOrTablet && outstreamPlacement && this.adType === 'square' && index === 1) {
        this.placeOutstreamAd(outstreamPlacement, adProps);
      }

      window._dlf.adxtag(placement, options);
    },
    placeOutstreamAd(placement, adProps) {
      const script = window.document.createElement('script');
      script.src = '//s1.adform.net/banners/scripts/video/outstream/inview.js';
      script.dataset.pmpId = `${placement}&mkv=${adProps.mkv}&mkw=${adProps.mkw}`;
      script.dataset.soundOnhover = 'false';

      const div = window.document.createElement('div');
      div.id = 'adform-outstream';
      div.style.height = '0px';
      div.style.overflow = 'hidden';
      div.appendChild(script);

      const dom = window.document.getElementById(adProps.dom) as HTMLElement;
      const target = dom.parentElement!.previousElementSibling?.previousElementSibling as HTMLElement;
      target.before(div);
    },
    getAdFormType(adType) {
      const fallBackAdFormType = mobileFallbackAds[adType] || mobileAds.find((ad) => ad === adType) || null;

      return {
        type: this.isMobileOrTablet ? fallBackAdFormType : (adType as AdFormPlacementType),
        isFallback: this.isMobileOrTablet && fallBackAdFormType !== null,
      };
    },
    parseDiMatterAd(placement) {
      if (this.isMobileOrTablet && this.settings.fallback) {
        placement = this.settings.fallback;
      }

      return /DIM:/i.test(placement) ? placement.replace(/DIM:/i, '').split(',') : false;
    },
  },
  async mounted() {
    if (this.isAdBlocked) {
      this.adBlockFallback = this.settings.adBlockFallback;
      return;
    }

    if (!this.divId) {
      const dimatter = this.settings.mid && this.parseDiMatterAd(this.settings.mid);
      if (Array.isArray(dimatter)) {
        this.divId = dimatter[0];
      } else {
        const rnd = Math.floor(10000 + Math.random() * 90000);
        this.divId = this.settings.divId ? this.settings.divId : `ad-${rnd}`;
      }

      await this.$nextTick();
    }

    window._dlf = window._dlf || {};

    if (typeof window._dlf.adxtag !== 'function') {
      loadScript(adxUrl);
    }

    await this.initAdx();

    if (this.adFormType === 'scroller') {
      const scroller = this.$refs[`${this.adFormType}-${this.divId}`] as HTMLDivElement;
      this.observer = new MutationObserver((mutations: any) => {
        this.$nextTick(() => {
          const mutation = mutations[0] as MutationRecord;
          const nodes = mutation.addedNodes as NodeList;
          const node = nodes[0];
          if (node.nodeName === 'ADFM-AD') {
            const element = node as HTMLElement;
            const iframe = element.querySelector('iframe') as HTMLIFrameElement;
            const iframeBody = iframe.contentDocument as any;
            new MutationObserver((mutations: any) => {
              this.$nextTick(() => {
                const mutation = mutations[0] as MutationRecord;
                if (mutation.removedNodes.length) {
                  const nodes = mutation.removedNodes as NodeList;
                  const node = nodes[0] as HTMLElement;
                  if (node.id === 'adf-banner') {
                    scroller.style.display = 'none';
                  }
                }
              });
            }).observe(iframeBody, { subtree: true, childList: true });
          }
        });
      });
      this.observer.observe(scroller, { subtree: true, childList: true });
    }
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
});
